import { Condition, ConditionSet, ConditionType } from '../types';
import { Add, Delete } from '@mui/icons-material';
import { Box, Typography, IconButton, Button, Chip, Tooltip } from '@mui/material';
import { ReactNode } from 'react';
import MaterialConditionInput from './MaterialConditionInput';
import SoilConditionInput from './SoilConditionInput';
import EdgeConditionInput from './EdgeConditionInput';
import { useTranslation } from 'react-i18next';
import { DEFAULT_CONDITIONS, MATERIAL_OPTIONS } from '../constants';

interface MaterialConditionSetProps {
  index: number;
  conditionSet: ConditionSet;
  onDelete: (id: string) => void;
  onUpdate: (updatedRule: ConditionSet) => void;
}

export default function MaterialConditionSet({ index, conditionSet, onDelete, onUpdate }: MaterialConditionSetProps) {
  const { t } = useTranslation();

  const hasEdgeCondition = conditionSet.conditions.some((c) => c.type === 'edge');
  const hasOtherMaterial = conditionSet.conditions.some((c) => c.type === 'material' && c.className !== 'soil');
  const hasSoilMaterial = conditionSet.conditions.some((c) => c.type === 'material' && c.className === 'soil');
  const hasSoilCondition = conditionSet.conditions.some((c) => c.type === 'soil');

  const isSoilDisabled = hasEdgeCondition || hasOtherMaterial;
  const isEdgeDisabled = hasSoilCondition || hasSoilMaterial;

  const availableMaterials = hasSoilCondition
    ? MATERIAL_OPTIONS.filter((material) => material.className === 'soil')
    : hasEdgeCondition
      ? MATERIAL_OPTIONS.filter((material) => material.className !== 'soil')
      : MATERIAL_OPTIONS;

  const handleUpdate = (updates: Partial<ConditionSet>) => {
    onUpdate({ ...conditionSet, ...updates });
  };

  const handleAddCondition = (type: ConditionType) => {
    let condition: Condition;
    switch (type) {
      case 'material':
        condition = { ...DEFAULT_CONDITIONS.material };
        break;
      case 'soil':
        condition = { ...DEFAULT_CONDITIONS.soil };
        break;
      case 'edge':
        condition = { ...DEFAULT_CONDITIONS.edge };
        break;
      default:
        return;
    }
    handleUpdate({ conditions: [...conditionSet.conditions, condition] });
  };

  const handleDeleteCondition = (conditionIndex: number) => {
    handleUpdate({
      conditions: conditionSet.conditions.filter((_, i) => i !== conditionIndex),
    });
  };

  const handleUpdateCondition = (conditionIndex: number, updates: Partial<Condition>) => {
    handleUpdate({
      conditions: conditionSet.conditions.map((cond, i) =>
        i === conditionIndex ? ({ ...cond, ...updates } as Condition) : cond
      ),
    });
  };

  return (
    <Box
      sx={{
        border: '1px solid #eeeeee',
        borderRadius: '5px',
        padding: '1em',
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '2em',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
        <Box>
          <Typography variant='h6'>
            {t('billableMaterial.rule')} {index + 1}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <IconButton onClick={() => onDelete(conditionSet.id)}>
            <Delete />
          </IconButton>
        </Box>
      </Box>

      {!conditionSet || conditionSet.conditions.length === 0 ? (
        <Typography variant='body1'>{t('billableMaterial.noConditionConfigured')}</Typography>
      ) : (
        <Typography variant='body2' marginBottom='3em'>
          {t('billableMaterial.conditionsInfo')}
        </Typography>
      )}

      {conditionSet.conditions.map((condition, conditionIndex) => {
        switch (condition.type) {
          case 'material':
            return (
              <ConditionInputLayout
                label='material'
                key={`condition-material-${conditionIndex}`}
                onDelete={() => handleDeleteCondition(conditionIndex)}
              >
                <MaterialConditionInput
                  condition={condition}
                  onChange={(updates) => handleUpdateCondition(conditionIndex, updates)}
                  availableClasses={availableMaterials}
                />
              </ConditionInputLayout>
            );

          case 'soil':
            return (
              <ConditionInputLayout
                label='soil'
                key={`condition-soil-${conditionIndex}`}
                onDelete={() => handleDeleteCondition(conditionIndex)}
              >
                <SoilConditionInput
                  condition={condition}
                  onChange={(updates) => handleUpdateCondition(conditionIndex, updates)}
                />
              </ConditionInputLayout>
            );

          case 'edge':
            return (
              <ConditionInputLayout
                label='edge'
                key={`condition-edge-${conditionIndex}`}
                onDelete={() => handleDeleteCondition(conditionIndex)}
              >
                <EdgeConditionInput
                  condition={condition}
                  onChange={(updates) => handleUpdateCondition(conditionIndex, updates)}
                />
              </ConditionInputLayout>
            );

          default:
            return null;
        }
      })}

      <Box sx={{ display: 'flex', gap: '1em', marginTop: 3 }}>
        <AddCondtionButton
          text={t('billableMaterial.conditionTypes.material')}
          onClick={() => handleAddCondition('material')}
        />
        <AddCondtionButton
          text={t('billableMaterial.conditionTypes.soil')}
          onClick={() => handleAddCondition('soil')}
          disabled={isSoilDisabled}
          disabledInfo='billableMaterial.tooltip.soilDisabled'
        />
        <AddCondtionButton
          text={t('billableMaterial.conditionTypes.edge')}
          onClick={() => handleAddCondition('edge')}
          disabled={isEdgeDisabled}
          disabledInfo='billableMaterial.tooltip.edgeDisabled'
        />
      </Box>
    </Box>
  );
}

interface ConditionInputLayoutProps {
  label: string;
  onDelete: () => void;
  children: ReactNode;
}

function ConditionInputLayout({ label, onDelete, children }: ConditionInputLayoutProps) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1, marginBottom: '2em' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          flex: 1,
          gap: '3em',
        }}
      >
        <Chip label={t(`billableMaterial.conditionTypes.${label}`)} variant='outlined' sx={{ width: '14em' }} />
        <Box sx={{ flex: 4 }}>{children}</Box>
      </Box>
      <IconButton onClick={() => onDelete()}>
        <Delete />
      </IconButton>
    </Box>
  );
}

interface AddCondtionButtonProps {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  disabledInfo?: string;
}

function AddCondtionButton({ text, onClick, disabled = false, disabledInfo = '' }: AddCondtionButtonProps) {
  const { t } = useTranslation();

  return (
    <Tooltip title={disabled ? t(disabledInfo) : undefined}>
      <Box>
        <Button onClick={onClick} startIcon={<Add />} size='small' disabled={disabled}>
          {text}
        </Button>
      </Box>
    </Tooltip>
  );
}
